import { useState } from 'react';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { Task } from '../../services/api/tasksService';
import sitePlanUrl from '../../assets/siteplan/fe-siteplan-app.svg';
import sitePlanLocations from '../../assets/siteplan/fe-siteplan-locations.json';

interface SiteLocationRecord {
  location_code: string;
  name: string;
  location_type: string;
  obq_location_id?: string | null;
  obq_project?: string | null;
  polygon_points?: number[][];
  centroid_svg?: number[];
}

interface SitePlanFile {
  site_map: {
    view_box: string;
    effective_from?: string;
  };
  locations: SiteLocationRecord[];
}

interface SitePlanMapProps {
  task?: Partial<Task> | null;
  compact?: boolean;
}

const typedSitePlan = sitePlanLocations as unknown as SitePlanFile;
const LOT_CODE_PATTERN = /\bLOT[-\s_]?0*(\d{1,3})\b/i;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

const normalizeToken = (value?: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-');

const taskTokens = (task?: Partial<Task> | null) => {
  if (!task) return new Set<string>();
  const rawValues = [
    task.workLocationId,
    task.workLocation,
    task.workLocationRef?.name,
    task.taskCode,
    task.title,
    task.component?.code,
    task.component?.name,
    task.component?.unit?.code,
    task.component?.unit?.name,
  ];
  const tokens = new Set(rawValues.map(normalizeToken).filter(Boolean));
  rawValues.forEach((value) => {
    const match = String(value ?? '').match(LOT_CODE_PATTERN);
    if (match) tokens.add(`LOT-${match[1].padStart(3, '0')}`);
  });
  return tokens;
};

const locationMatchesTask = (location: SiteLocationRecord, tokens: Set<string>) => {
  if (!tokens.size) return false;
  const locationTokens = [
    location.location_code,
    location.name,
    location.obq_location_id,
    location.obq_project,
  ].map(normalizeToken);
  return locationTokens.some((token) => token && tokens.has(token));
};

const polygonPoints = (location: SiteLocationRecord) =>
  (location.polygon_points ?? [])
    .filter((point) => point.length >= 2)
    .map(([x, y]) => `${x},${y}`)
    .join(' ');

const centroid = (location: SiteLocationRecord): [number, number] | null =>
  location.centroid_svg && location.centroid_svg.length >= 2
    ? [Number(location.centroid_svg[0]), Number(location.centroid_svg[1])]
    : null;

export default function SitePlanMap({ task, compact = false }: SitePlanMapProps) {
  const [zoom, setZoom] = useState(1);
  const tokens = taskTokens(task);
  const matchedLocations = typedSitePlan.locations.filter((location) => locationMatchesTask(location, tokens));
  const highlightedLocations = matchedLocations.length
    ? matchedLocations
    : typedSitePlan.locations.filter((location) => location.location_code === 'SITE');
  const primaryLocation = matchedLocations[0] ?? null;
  const zoomIn = () => setZoom((current) => Math.min(MAX_ZOOM, Number((current + ZOOM_STEP).toFixed(2))));
  const zoomOut = () => setZoom((current) => Math.max(MIN_ZOOM, Number((current - ZOOM_STEP).toFixed(2))));
  const resetZoom = () => setZoom(1);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
        <Chip
          size="small"
          color={primaryLocation ? 'primary' : 'default'}
          label={primaryLocation ? primaryLocation.location_code : 'Site plan'}
        />
        <Typography variant="body2" fontWeight={800}>
          {primaryLocation ? primaryLocation.name : 'Fairmont Phase 1B'}
        </Typography>
      </Stack>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '779.8 / 1193.9',
          maxHeight: compact ? 360 : 560,
          minHeight: compact ? 260 : 340,
          bgcolor: '#f8faf7',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'auto',
          scrollbarWidth: 'thin',
          '@keyframes sitePlanPulse': {
            '0%': { opacity: 0.35, transform: 'scale(0.92)' },
            '50%': { opacity: 1, transform: 'scale(1.08)' },
            '100%': { opacity: 0.35, transform: 'scale(0.92)' },
          },
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            position: 'sticky',
            top: 8,
            right: 8,
            zIndex: 3,
            ml: 'auto',
            mb: -5.25,
            width: 'max-content',
            p: 0.5,
            borderRadius: 1,
            bgcolor: 'rgba(255,255,255,0.92)',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.14)',
          }}
        >
          <Tooltip title="Zoom out">
            <span>
              <IconButton aria-label="Zoom out site plan" size="small" onClick={zoomOut} disabled={zoom <= MIN_ZOOM}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reset zoom">
            <span>
              <IconButton aria-label="Reset site plan zoom" size="small" onClick={resetZoom} disabled={zoom === MIN_ZOOM}>
                <CenterFocusStrongIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom in">
            <span>
              <IconButton aria-label="Zoom in site plan" size="small" onClick={zoomIn} disabled={zoom >= MAX_ZOOM}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Box
          sx={{
            position: 'relative',
            width: `${zoom * 100}%`,
            minWidth: '100%',
            aspectRatio: '779.8 / 1193.9',
            mx: zoom === 1 ? 0 : 'auto',
          }}
        >
          <Box
            component="img"
            src={sitePlanUrl}
            alt="Fairmont Phase 1B site plan"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
          />
          <Box
            component="svg"
            viewBox={typedSitePlan.site_map.view_box}
            preserveAspectRatio="xMidYMid meet"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
          >
            {highlightedLocations.map((location) => {
              const center = centroid(location);
              const selected = location.location_code === primaryLocation?.location_code;
              return (
                <g key={location.location_code}>
                  {location.polygon_points?.length ? (
                    <polygon
                      points={polygonPoints(location)}
                      fill={selected ? 'rgba(25, 118, 210, 0.24)' : 'rgba(76, 175, 80, 0.08)'}
                      stroke={selected ? '#0d47a1' : 'rgba(46, 125, 50, 0.45)'}
                      strokeWidth={selected ? 3 : 1.5}
                    />
                  ) : null}
                  {center ? (
                    <>
                      <circle cx={center[0]} cy={center[1]} r={selected ? 12 : 7} fill={selected ? '#1976d2' : '#2e7d32'} opacity="0.92" />
                      {selected ? (
                        <circle
                          cx={center[0]}
                          cy={center[1]}
                          r="24"
                          fill="none"
                          stroke="#1976d2"
                          strokeWidth="4"
                          style={{ animation: 'sitePlanPulse 1.5s ease-in-out infinite', transformOrigin: `${center[0]}px ${center[1]}px` }}
                        />
                      ) : null}
                      <text x={center[0] + 15} y={center[1] + 5} fontSize="13" fontWeight="900" fill={selected ? '#0d47a1' : '#2e7d32'}>
                        {location.location_code}
                      </text>
                    </>
                  ) : null}
                </g>
              );
            })}
          </Box>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        {primaryLocation
          ? `${primaryLocation.location_type.replace(/_/g, ' ')} location shown for this task.`
          : 'Task-specific map position is not linked yet, so the full Phase 1B plan is shown.'}
      </Typography>
    </Box>
  );
}
